import type { XYPosition } from "@xyflow/react";
import { createContext, type PropsWithChildren,useCallback, useContext, useMemo, useState } from "react";

import type { RuleEdgeType } from "../types/edges";

/** Fields passed to `openMenu` (everything except `anchorEl`). */
export type AddNodeMenuOpenPayload = {
	sourceNodeId?: string;
	sourceHandleId?: string | null;
	targetNodeId?: string;
	edgeId?: string;
};

export type AddNodeMenuContextData = {
	anchorEl: HTMLElement | null;
} & AddNodeMenuOpenPayload;

type AddNodeMenuContextValue = {
	anchorEl: HTMLElement | null;
	isOpen: boolean;
	/** Node the menu was opened against — used to filter the dropdown by adjacency. */
	sourceNodeId?: string;
	openMenu: (anchor: HTMLElement, context?: AddNodeMenuOpenPayload) => void;
	closeMenu: () => void;
	selectItem: (itemKey: string) => void;
	createTemporaryEdge?: (sourceNodeId: string, sourceHandleId: string, targetPosition: XYPosition, edgeType?: RuleEdgeType) => void;
	removeTemporaryEdge?: () => void;
};

interface AddNodeMenuProviderProps {
	onItemSelect?: (itemKey: string, context: AddNodeMenuContextData) => void;
	createTemporaryEdge?: (sourceNodeId: string, sourceHandleId: string, targetPosition: XYPosition, edgeType?: RuleEdgeType) => void;
	removeTemporaryEdge?: () => void;
}

const AddNodeMenuContext = createContext<AddNodeMenuContextValue | undefined>(undefined);

export const AddNodeMenuProvider = ({ children, onItemSelect, createTemporaryEdge, removeTemporaryEdge }: PropsWithChildren<AddNodeMenuProviderProps>) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [menuContext, setMenuContext] = useState<AddNodeMenuOpenPayload>({});

	const closeMenu = useCallback(() => {
		setAnchorEl(null);
		setMenuContext({});
		removeTemporaryEdge?.();
	}, [removeTemporaryEdge]);

	const openMenu = useCallback((anchor: HTMLElement, context?: AddNodeMenuOpenPayload) => {
		setAnchorEl((currentAnchor) => {
			const isSameAnchor = currentAnchor === anchor;

			if (isSameAnchor) {
				setMenuContext({});
				return null;
			}

			setMenuContext(context ?? {});
			return anchor;
		});
	}, []);

	const selectItem = useCallback((itemKey: string) => {
		if (onItemSelect) {
			onItemSelect(itemKey, { anchorEl, ...menuContext });
		}
		closeMenu();
	}, [anchorEl, menuContext, onItemSelect, closeMenu]);

	const value = useMemo<AddNodeMenuContextValue>(() => ({
		anchorEl,
		isOpen: Boolean(anchorEl),
		sourceNodeId: menuContext.sourceNodeId,
		openMenu,
		closeMenu,
		selectItem,
		createTemporaryEdge,
		removeTemporaryEdge,
	}), [anchorEl, menuContext.sourceNodeId, openMenu, closeMenu, selectItem, createTemporaryEdge, removeTemporaryEdge]);

	return (
		<AddNodeMenuContext.Provider value={value}>
			{children}
		</AddNodeMenuContext.Provider>
	);
};

export const useAddNodeMenu = () => {
	const context = useContext(AddNodeMenuContext);

	if (!context) {
		throw new Error("useAddNodeMenu must be used within an AddNodeMenuProvider");
	}

	return context;
};
